import { model as authorizePaymentModel } from '$/authorize-payment'
import { model as cartModel } from '$/cart'
import { model as creditCardModel } from '$/credit-card'
import { api, type ApiError } from '@setplex/tria-api'
import { createEvent, createStore, sample, split } from 'effector'
import { reset } from 'patronum/reset'

import { WBSPaymentErrorCodes } from '../../interfaces'
import {
  closePaymentPopup,
  closePaymentPopupWithType,
  resetErrors,
} from './model'

export const $paymentError = createStore<string>('')

// TODO this is specific channel errors - should be moved to authorize/model
export const $paymentFormCreditCardNumberError = createStore<string>('')
  .on(authorizePaymentModel.$creditCardNumberError, (_, error) => error)
  .on(creditCardModel.$creditCardNumberError, (_, error) => error)

export const $paymentFormCreditCardExpirationTimeError = createStore<string>('')
  .on(authorizePaymentModel.$creditCardExpirationTimeError, (_, error) => error)
  .on(creditCardModel.$creditCardExpirationTimeError, (_, error) => error)

export const $paymentFormCreditCardCvvError = createStore<string>('')
  .on(authorizePaymentModel.$creditCardCvvError, (_, error) => error)
  .on(creditCardModel.$creditCardCvvError, (_, error) => error)

const handlePaymentError = createEvent<ApiError>()
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleHttpPaymentError = createEvent<any>()
const handleLogicPaymentError = createEvent<ApiError>()

// Reset error after user interactions
// Reset input fields errors after user interactions
reset({
  clock: resetErrors,
  target: [
    $paymentError,
    creditCardModel.$creditCardNumberError,
    creditCardModel.$creditCardExpirationTimeError,
    creditCardModel.$creditCardCvvError,
  ],
})

sample({
  clock: [
    cartModel.postItemToCartFx.failData,
    api.creditCards.getSavedCreditCardsFx.failData,
    api.cart.setPaymentDataCartFx.failData,
    api.cart.checkoutSelectedContentItemFx.failData,
    api.cart.confirmCartPurchaseFx.failData,
    api.purchases.initializePurchaseProcessFx.failData,
  ],
  target: handlePaymentError,
})

split({
  source: handlePaymentError,
  match: {
    handleLogicPaymentError: ({ payload }) => Boolean(payload?.errorCode),
    handleHttpPaymentError: ({ payload }) => !payload?.errorCode,
  },
  cases: {
    handleHttpPaymentError,
    handleLogicPaymentError,
  },
})

// Handle http errors only for payment requests
sample({
  clock: handleHttpPaymentError,
  fn: ({ payload }) => {
    const isTimedOutError = payload === null

    if (isTimedOutError) return 'Request timed out'

    if (payload?.code >= 400 && payload?.code < 500) return 'Bad request'

    if (payload?.code >= 500) return 'Internal server error'

    return 'Unknown server error'
  },
  target: $paymentError,
})

// Handle WBS logic errors only for payment requests
sample({
  clock: handleLogicPaymentError,
  fn: ({ payload }) => {
    switch (payload.errorCode) {
      case WBSPaymentErrorCodes.WB0001:
        return 'Content Item is not available for purchase'
      case WBSPaymentErrorCodes.WB0005:
        return 'Payment method is not set'
      case WBSPaymentErrorCodes.WB0006:
        return "Currency in payment system config doesn't correspond to currency in subscription"
      case WBSPaymentErrorCodes.WB0007:
        return "There's no active shopping cart for the subscriber"
      case WBSPaymentErrorCodes.WB0013:
        return 'Original price was changed'
      case WBSPaymentErrorCodes.WB0014:
        return 'Invalid payment data'
      case WBSPaymentErrorCodes.WB0015:
        return 'Authorization payment transaction is failed'
      case WBSPaymentErrorCodes.WB0017:
        return 'Capture payment transaction is failed'
      case WBSPaymentErrorCodes.WB0018:
        return 'Void payment transaction is failed'
      case WBSPaymentErrorCodes.WB0019:
        return 'Refund payment transaction is failed'
      case WBSPaymentErrorCodes.WB0020:
        return 'Content set items were changed'
      default:
        return 'Unknown error'
    }
  },
  target: $paymentError,
})

sample({
  clock: [closePaymentPopup, closePaymentPopupWithType],
  target: resetErrors,
})
